import type { ReferenceImage } from '@/components/MessageList/ReferenceUIMessage/const';
import type { ChatInputMessage } from '@/modules/ChatInput/types';
import type { ChatMessage, ToolCallMessage } from '@/stores/chat';
import { transformReferenceUIData } from '@/components/ToolCallMessage/Tools/transformer';
import { safeJSONParse } from '@/utils/json';
import { buildContinueGenerationInputContent } from './continueGeneration';

export const CONFIRM_REFERENCE_UI_SELECTION_TOOL_NAME = 'confirm_reference_ui_selection';

interface ReferenceSelectionToolCall {
  toolName: string;
  status: string;
  arguments?: string;
  output?: string;
}

interface ReferenceSelectionToolPayload {
  selectedReferenceIndex?: unknown;
  startGeneration?: unknown;
  generationAccepted?: unknown;
  selectionAccepted?: unknown;
}

export interface ModelConfirmedReferenceSelection {
  reference: ReferenceImage;
  startGeneration: boolean;
}

export function getLatestReferenceImages(messages: readonly ChatMessage[]): ReferenceImage[] {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role !== 'tool_call' || message.toolName !== 'gen_reference_ui' || message.status !== 'completed') {
      continue;
    }

    return transformReferenceUIData(message as ToolCallMessage).referenceImages.filter(image => Boolean(image.url));
  }

  return [];
}

function parseReferenceSelectionToolPayload(value?: string): ReferenceSelectionToolPayload | null {
  const payload = safeJSONParse<ReferenceSelectionToolPayload>(value || '');
  return payload && typeof payload === 'object' ? payload : null;
}

export function resolveReferenceSelectionToolCall(
  toolCall: ReferenceSelectionToolCall,
  images: readonly ReferenceImage[],
): ModelConfirmedReferenceSelection | undefined {
  if (
    toolCall.toolName !== CONFIRM_REFERENCE_UI_SELECTION_TOOL_NAME
    || toolCall.status !== 'completed'
    || images.length === 0
  ) {
    return undefined;
  }

  const output = parseReferenceSelectionToolPayload(toolCall.output);
  const args = parseReferenceSelectionToolPayload(toolCall.arguments);
  if (output?.selectionAccepted === false) {
    return undefined;
  }
  const selectedReferenceIndex = Number(output?.selectedReferenceIndex ?? args?.selectedReferenceIndex);
  const reference = Number.isInteger(selectedReferenceIndex)
    ? images[selectedReferenceIndex - 1]
    : undefined;
  if (!reference?.url) {
    return undefined;
  }

  return {
    reference,
    startGeneration: output?.generationAccepted === true || output?.startGeneration === true,
  };
}

export function resolveAttachedReferenceSelection(
  input: readonly ChatInputMessage[],
  selectedReference?: ReferenceImage,
  referenceImages: readonly ReferenceImage[] = [],
): ReferenceImage | undefined {
  const attachedImageUrls = new Set(
    input
      .filter((item): item is Extract<ChatInputMessage, { type: 'input_image' }> => item.type === 'input_image')
      .map(item => item.image),
  );

  if (selectedReference?.url && attachedImageUrls.has(selectedReference.url)) {
    return selectedReference;
  }

  return referenceImages.find(image => image.url && attachedImageUrls.has(image.url));
}

export function resolveConfirmedReferenceFromConversation(
  messages: readonly ChatMessage[],
): ReferenceImage | undefined {
  for (let referenceIndex = messages.length - 1; referenceIndex >= 0; referenceIndex -= 1) {
    const referenceMessage = messages[referenceIndex];
    if (
      referenceMessage.role !== 'tool_call'
      || referenceMessage.toolName !== 'gen_reference_ui'
      || referenceMessage.status !== 'completed'
    ) {
      continue;
    }

    const images = transformReferenceUIData(referenceMessage as ToolCallMessage).referenceImages.filter(image =>
      Boolean(image.url),
    );

    for (let messageIndex = messages.length - 1; messageIndex > referenceIndex; messageIndex -= 1) {
      const message = messages[messageIndex];
      if (message.role === 'tool_call') {
        const modelSelection = resolveReferenceSelectionToolCall(message, images);
        if (modelSelection) {
          return modelSelection.reference;
        }
        continue;
      }

      if (message.role !== 'user') {
        continue;
      }

      const attachedImageUrls = new Set(message.files?.map(file => file.file_url).filter(Boolean));
      const selectedReference = images.find(image => attachedImageUrls.has(image.url));
      if (selectedReference) {
        return selectedReference;
      }
    }

    return undefined;
  }

  return undefined;
}

export function buildConfirmedInlineReferenceInput(
  input: readonly ChatInputMessage[],
  displayText: string,
  reference: ReferenceImage,
): ChatInputMessage[] {
  const confirmedText = buildContinueGenerationInputContent(displayText, {
    hasReferenceImage: true,
    referenceUiConfirmed: true,
  });
  const nextInput = input.filter(item => item.type !== 'input_text');
  const hasReferenceImage = nextInput.some(item => item.type === 'input_image' && item.image === reference.url);

  return [
    { type: 'input_text', text: confirmedText },
    ...nextInput,
    ...(!hasReferenceImage
      ? [
          {
            type: 'input_image' as const,
            image: reference.url,
            preserveMultimodal: true,
          },
        ]
      : []),
  ];
}
